import { useContext, useState } from 'react';
import { Box } from '@mui/joy';
import { useNavigate } from 'react-router';

import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import AddCommentRoundedIcon from '@mui/icons-material/AddCommentRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import LoyaltyRoundedIcon from '@mui/icons-material/LoyaltyRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';

import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

import { LayoutContext } from '../../context';
import { APICreateUnion, APIGetMyUnions } from '../../api';

/**
 * Карточка взаимной симпатии.
 *
 * Логика не менялась: те же вызовы onConfirmMatch / onCreateChat, тот же
 * переход в чат и то же создание союза. Переделано только оформление:
 * фото на всю карточку с плавным затемнением, имя и город поверх фото,
 * метка суперлайка, интересы и кнопки одинаковой ширины внизу.
 */

const TimeAgo = ({ datetime }) => {
  if (!datetime) return null;

  const date = new Date(datetime);
  const timeAgo = formatDistanceToNow(date, { addSuffix: true, locale: ru });

  return timeAgo
    .replace('about', '')
    .replace('less than a minute', 'только что')
    .replace('minute', 'мин.')
    .replace('hour', 'час')
    .replace('day', 'день')
    .replace('days', 'дней')
    .replace('ago', 'назад')
    .trim();
};

export default function MatchListItem({ match, onSelect, selected, onConfirmMatch, onCreateChat, index = 0 }) {
  const navigate = useNavigate();
  const { token } = useContext(LayoutContext);
  const [isCreatingUnion, setIsCreatingUnion] = useState(false);

  const isSuperLike = match.is_super_like;
  const isMutual = match.match_status === 'mutual';
  const likedYou = match.match_status === 'liked_you';

  // Создание/открытие союза — логика прежняя
  const handleCreateOrOpenUnion = async (e) => {
    e.stopPropagation();

    try {
      setIsCreatingUnion(true);

      const myUnionsResponse = await APIGetMyUnions(token);
      const myUnions = myUnionsResponse.unions || [];

      const existingUnion = myUnions.find(union =>
        union.members?.some(member => member.profile?.id === match.profile?.id)
      );

      if (existingUnion) {
        navigate(`/union/${existingUnion.id}`);
      } else {
        const unionData = {
          name: '',
          description: '',
          partner_profile_id: match.profile?.id,
        };

        const createResponse = await APICreateUnion(token, unionData);

        if (createResponse.success) {
          navigate(`/union/${createResponse.union_id}`);
        }
      }
    } catch (error) {
      console.error('Ошибка при работе с союзом:', error);
    } finally {
      setIsCreatingUnion(false);
    }
  };

  if (!match.profile) return null;

  const hobbies = match.profile.hobby
    ? match.profile.hobby.split(',').map(h => h.trim()).filter(Boolean).slice(0, 3)
    : [];

  return (
    <Box
      className="lvc-card lvc-card--hover lvc-in"
      onClick={() => onSelect?.(match)}
      style={{ animationDelay: `${Math.min(index * 0.05, 0.4)}s` }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        outline: selected ? '2px solid rgba(138, 77, 255, .45)' : 'none',
        outlineOffset: '-2px',
      }}
    >
      {/* Фото */}
      <div className="lvc-person">
        {match.profile?.photo_file_path ? (
          <img
            className="lvc-person__photo"
            src={match.profile.photo_file_path}
            loading="lazy"
            alt={match.profile?.name || 'Анкета'}
          />
        ) : (
          <div className="lvc-person__empty">
            <PersonRoundedIcon sx={{ fontSize: 64 }} />
          </div>
        )}

        <div className="lvc-person__shade" />

        <div className="lvc-tagrow">
          {isSuperLike && (
            <span className="lvc-tag lvc-tag--super">
              <StarRoundedIcon sx={{ fontSize: 14 }} />
              Суперлайк
            </span>
          )}
          {likedYou && (
            <span className="lvc-tag lvc-tag--wait">
              <FavoriteRoundedIcon sx={{ fontSize: 13 }} />
              Лайкнул(а) вас
            </span>
          )}
          {isMutual && (
            <span className="lvc-tag lvc-tag--new">
              <FavoriteRoundedIcon sx={{ fontSize: 13 }} />
              Взаимно
            </span>
          )}
        </div>

        <div className="lvc-person__body">
          <h3 className="lvc-person__name">
            {match.profile.name}{match.profile.age ? `, ${match.profile.age}` : ''}
          </h3>
          <p className="lvc-person__meta">
            {match.profile.city ? (
              <>
                <PlaceRoundedIcon sx={{ fontSize: 14 }} />
                {match.profile.city}
              </>
            ) : null}
          </p>
        </div>
      </div>

      {/* Интересы */}
      {hobbies.length > 0 && (
        <Box className="lvc-row" sx={{ px: 1.75, pt: 1.5 }}>
          {hobbies.map((hobby, i) => (
            <span className="lvc-hobby" key={i}>{hobby}</span>
          ))}
        </Box>
      )}

      {/* Кнопки */}
      <Box sx={{ mt: 'auto' }}>
        <div className="lvc-actions">
          <button
            type="button"
            className={`lvc-btn ${likedYou ? 'lvc-btn--like' : 'lvc-btn--done'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (likedYou) onConfirmMatch(match.interaction_id);
            }}
          >
            {likedYou ? <FavoriteBorderRoundedIcon sx={{ fontSize: 17 }} /> : <FavoriteRoundedIcon sx={{ fontSize: 17 }} />}
            {likedYou ? 'Лайкнуть в ответ' : 'Взаимная симпатия'}
          </button>

          <div className="lvc-actions lvc-actions--row" style={{ padding: 0 }}>
            {isMutual && !match.chat_created && (
              <button
                type="button"
                className="lvc-btn lvc-btn--primary lvc-btn--sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateChat(match.interaction_id);
                }}
              >
                <AddCommentRoundedIcon sx={{ fontSize: 16 }} />
                Создать чат
              </button>
            )}

            {match.chat_created && (
              <button
                type="button"
                className="lvc-btn lvc-btn--primary lvc-btn--sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/chats?chatId=${match.chat_message}`);
                }}
              >
                <ChatRoundedIcon sx={{ fontSize: 16 }} />
                В чат
              </button>
            )}

            {(isMutual || match.chat_created) && (
              <button
                type="button"
                className="lvc-btn lvc-btn--soft lvc-btn--sm"
                disabled={isCreatingUnion}
                onClick={handleCreateOrOpenUnion}
              >
                <LoyaltyRoundedIcon sx={{ fontSize: 16 }} />
                {isCreatingUnion ? 'Минуту…' : 'Союз'}
              </button>
            )}
          </div>
        </div>

        <Box
          className="lvc-row lvc-muted"
          sx={{ px: 1.75, pb: 1.5, mt: -0.5, fontSize: 11.5, justifyContent: 'flex-end' }}
        >
          <ScheduleRoundedIcon sx={{ fontSize: 13 }} />
          <TimeAgo datetime={match.date_create} />
        </Box>
      </Box>
    </Box>
  );
}
